import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Initials, Notice, Spinner } from '../../components/ui';
import { Dialog } from '../../components/Dialog';
import { api, ApiError } from '../../lib/api';
import { relativeDate } from '../../lib/school';
import { Pager } from '../../components/Pager';
import { ManagePerson, type AdminPerson } from './ManagePerson';
import '../Page.css';
import './Admin.css';

interface SchoolRow {
  id: string;
  name: string;
  type?: 'organisation' | 'personal';
  paused_at?: string | null;
  invite_code: string | null;
  trust_name: string | null;
  seat_limit: number | null;
  created_at: string;
  onboarded_at: string | null;
  /** done: finished with every answer; no_owner: nobody to set it up yet; in_progress: an owner still has to finish. */
  setup?: 'done' | 'no_owner' | 'in_progress';
  /** Required answers still missing, e.g. "priority areas". */
  setup_missing?: string[];
  members: number;
  /** EducAitor admins in this organisation: not counted as people, but their lessons are. */
  admins?: number;
  leaders: number;
  lessons: number;
  last_lesson_at: string | null;
  /** The person in a personal account (or the first owner), and an open owner invite's email. */
  person_name?: string | null;
  person_email?: string | null;
  invited_email?: string | null;
}

type View = 'orgs' | 'personal' | 'unattached';

const PAGE_SIZE = 25;

interface PersonRow {
  id: string;
  name: string;
  email: string;
  role: string;
  is_owner: number;
  school_id: string | null;
  school_name: string | null;
  /** 'removed' (paused) after being taken out of pending_school_name. */
  join_status?: string;
  pending_school_name?: string | null;
  lessons: number;
  created_at: string;
  last_active_at: string | null;
}

const toPerson = (u: PersonRow): AdminPerson => ({
  id: u.id,
  name: u.name,
  email: u.email,
  role: u.role,
  isOwner: !!Number(u.is_owner),
  schoolId: u.school_id,
  schoolName: u.school_name,
  removedFrom: u.join_status === 'removed' ? (u.pending_school_name ?? 'their organisation') : null,
});

export function AdminSchoolsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  // Which kind of account a new one starts as (null: dialog closed).
  const [newType, setNewType] = useState<'organisation' | 'personal' | null>(null);
  // Teams, people on their own, or accounts in neither.
  // The tab is kept in the address (?view=personal), so "back" from an account returns to it.
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'personal' ? 'personal' : params.get('view') === 'unattached' ? 'unattached' : 'orgs';
  const setView = (v: View) => setParams(v === 'orgs' ? {} : { view: v }, { replace: true });
  const [managing, setManaging] = useState<AdminPerson | null>(null);
  // Arriving after deleting an organisation carries a line to show.
  const location = useLocation();
  const [message, setMessage] = useState<string | null>((location.state as { message?: string } | null)?.message ?? null);
  useEffect(() => setPage(1), [q, view]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const navigate = useNavigate();
  // One search box finds organisations, personal accounts and people.
  const people = useQuery({
    queryKey: ['admin-people', debounced],
    queryFn: async () => (await api<{ data: PersonRow[] }>(`/admin/users?q=${encodeURIComponent(debounced)}`)).data,
    enabled: view === 'orgs' && debounced.length >= 2,
  });
  const unattached = useQuery({
    queryKey: ['admin-unattached'],
    queryFn: async () => (await api<{ data: PersonRow[] }>('/admin/users?noSchool=1')).data,
  });
  const openPerson = (u: PersonRow) => (u.school_id ? navigate(`/admin/schools/${u.school_id}?person=${u.id}`) : setManaging(toPerson(u)));
  const type = view === 'personal' ? 'personal' : 'organisation';
  const schools = useQuery({
    queryKey: ['admin-schools', q, page, type],
    queryFn: () =>
      api<{ data: SchoolRow[]; total: number; counts?: { organisation: number; personal: number } }>(
        `/admin/schools?q=${encodeURIComponent(q)}&page=${page}&pageSize=${PAGE_SIZE}&type=${type}`,
      ),
    placeholderData: (prev) => prev,
  });
  const rows = schools.data?.data ?? [];
  const total = schools.data?.total ?? rows.length;
  const counts = schools.data?.counts;
  const loose = unattached.data ?? [];
  // Never in an organisation (signed up alone), versus taken out of one or still waiting.
  const neverJoined = loose.filter((u) => !u.join_status || u.join_status === 'active');
  const leftOrWaiting = loose.filter((u) => u.join_status && u.join_status !== 'active');

  const tabs: { id: View; label: string; count?: number }[] = [
    { id: 'orgs', label: 'Organisations', count: counts?.organisation },
    { id: 'personal', label: 'Personal accounts', count: counts?.personal },
    { id: 'unattached', label: 'No organisation', count: unattached.data?.length },
  ];

  return (
    <div className="page">
      <header className="admin-head">
        <div className="page-head">
          <p className="eyebrow">EducAitor admin</p>
          <h1>Organisations</h1>
          <p className="muted">
            Schools and teams, people using EducAitor on their own, and anyone not in either yet. New accounts are set up here: you choose the seats and
            invite the owner.
          </p>
        </div>
        <div className="admin-head-actions">
          <Button variant="secondary" onClick={() => setNewType('personal')}>
            New personal account
          </Button>
          <Button onClick={() => setNewType('organisation')}>New organisation</Button>
        </div>
      </header>
      <NewOrganisationDialog
        open={!!newType}
        initialType={newType ?? 'organisation'}
        onClose={() => setNewType(null)}
        onCreated={(id) => navigate(`/admin/schools/${id}`)}
      />

      <div className="admin-tabs" role="tablist" aria-label="Accounts">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={view === t.id} onClick={() => setView(t.id)}>
            {t.label}
            {t.count != null ? <span className="admin-tab-count">{t.count}</span> : null}
          </button>
        ))}
      </div>

      {view !== 'unattached' ? (
        <div className="search admin-search">
          <Field
            id="admin-school-search"
            label="Search"
            placeholder={view === 'personal' ? 'Name, email or code' : 'Organisation, code, trust, person or email'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      ) : null}
      {message ? <Notice tone="success">{message}</Notice> : null}
      <ManagePerson
        person={managing}
        onClose={() => setManaging(null)}
        onDone={(text) => {
          setManaging(null);
          setMessage(text);
          qc.invalidateQueries({ queryKey: ['admin-unattached'] });
          qc.invalidateQueries({ queryKey: ['admin-people'] });
          qc.invalidateQueries({ queryKey: ['admin-schools'] });
        }}
      />

      {view === 'unattached' ? (
        unattached.isLoading ? (
          <Spinner />
        ) : !loose.length ? (
          <div className="panel muted">Everyone is in an organisation or has a personal account.</div>
        ) : (
          <>
            {neverJoined.length ? (
              <section className="panel">
                <div className="admin-group-head">
                  <h2>Never joined an organisation</h2>
                  <p className="muted">
                    They signed up on their own and can&rsquo;t record yet. Open someone to give them a personal account, or add them to their school.
                  </p>
                </div>
                <PeopleList rows={neverJoined} loading={false} onOpen={openPerson} empty="" />
              </section>
            ) : null}
            {leftOrWaiting.length ? (
              <section className="panel">
                <div className="admin-group-head">
                  <h2>Removed, declined or waiting</h2>
                  <p className="muted">
                    Taken out of an organisation by a leader, turned down, or waiting for a leader to approve them. They keep their lessons; open someone to
                    add them back or move them.
                  </p>
                </div>
                <PeopleList rows={leftOrWaiting} loading={false} onOpen={openPerson} empty="" />
              </section>
            ) : null}
          </>
        )
      ) : schools.isLoading ? (
        <Spinner />
      ) : schools.isError ? (
        <Notice tone="error">Couldn&rsquo;t load {view === 'personal' ? 'personal accounts' : 'organisations'}.</Notice>
      ) : (
        <>
          {rows.length === 0 ? (
            <div className="panel muted">
              {q.trim()
                ? view === 'personal'
                  ? 'No personal accounts match.'
                  : 'No organisations match.'
                : view === 'personal'
                  ? 'No personal accounts yet.'
                  : 'No organisations yet.'}
            </div>
          ) : view === 'personal' ? (
            <PersonalTable rows={rows} onOpen={(s) => navigate(`/admin/schools/${s.id}`)} />
          ) : (
            <div className="panel table-wrap">
              <table className="table table-stack">
                <thead>
                  <tr>
                    <th>Organisation</th>
                    <th>Set up</th>
                    <th>People</th>
                    <th>Lessons</th>
                    <th>Last lesson</th>
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} className="clickable" onClick={() => navigate(`/admin/schools/${s.id}`)}>
                      <td>
                        <span className="cell-main">
                          {s.name}
                          {s.paused_at ? <span className="chip type-chip paused-chip">Paused</span> : null}
                        </span>
                        <span className="cell-sub">{[s.trust_name, s.invite_code].filter(Boolean).join(' · ') || 'No code yet'}</span>
                      </td>
                      <td data-label="Set up">
                        <SetupChip school={s} />
                      </td>
                      <td data-label="People">
                        <span className="cell-main">
                          {Number(s.members)}
                          {s.seat_limit != null ? <span className="faint"> of {s.seat_limit} seats</span> : null}
                        </span>
                        <span className="cell-sub">
                          {Number(s.leaders)} {Number(s.leaders) === 1 ? 'leader' : 'leaders'}
                          {Number(s.admins) ? ` · plus ${Number(s.admins)} EducAitor admin` : ''}
                        </span>
                      </td>
                      <td data-label="Lessons">{Number(s.lessons)}</td>
                      <td data-label="Last lesson">{s.last_lesson_at ? relativeDate(s.last_lesson_at) : '—'}</td>
                      <td className="row-go" aria-hidden="true">
                        <Chevron />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} noun={view === 'personal' ? 'personal accounts' : 'organisations'} />
          {view === 'orgs' && debounced.length >= 2 ? (
            <section className="panel">
              <h2 className="people-results-title">People</h2>
              <PeopleList rows={people.data ?? []} loading={people.isLoading} onOpen={openPerson} empty="No one matches." />
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function Chevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** People using EducAitor on their own: who, whether they're in yet, and how much they use it. */
function PersonalTable({ rows, onOpen }: { rows: SchoolRow[]; onOpen: (s: SchoolRow) => void }) {
  return (
    <div className="panel table-wrap">
      <table className="table table-stack">
        <thead>
          <tr>
            <th>Person</th>
            <th>Status</th>
            <th>Lessons</th>
            <th>Last lesson</th>
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const joined = !!s.person_email;
            return (
              <tr key={s.id} className="clickable" onClick={() => onOpen(s)}>
                <td>
                  <span className="cell-main">{s.person_name ?? s.name}</span>
                  <span className="cell-sub">{s.person_email ?? s.invited_email ?? 'No email yet'}</span>
                </td>
                <td data-label="Status">
                  {s.paused_at ? (
                    <span className="chip paused-chip">Paused</span>
                  ) : joined ? (
                    <span className="chip chip-accent">Active</span>
                  ) : (
                    <span className="chip" title="They haven't created their account from the email yet">
                      Invited
                    </span>
                  )}
                </td>
                <td data-label="Lessons">{Number(s.lessons)}</td>
                <td data-label="Last lesson">{s.last_lesson_at ? relativeDate(s.last_lesson_at) : '—'}</td>
                <td className="row-go" aria-hidden="true">
                  <Chevron />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Where someone with no organisation stands. */
function whereabouts(u: PersonRow): string {
  const org = u.pending_school_name ?? 'their organisation';
  if (u.join_status === 'pending') return `Waiting for approval at ${org}`;
  if (u.join_status === 'declined') return `Declined by ${org}`;
  if (u.join_status === 'removed') return `Removed from ${org}`;
  return 'Signed up on their own';
}

/** People from search or without an organisation; opening one goes to their organisation, or manages them here. */
function PeopleList({ rows, loading, onOpen, empty }: { rows: PersonRow[]; loading: boolean; onOpen: (u: PersonRow) => void; empty: string }) {
  if (loading) return <Spinner />;
  if (!rows.length) return <p className="faint">{empty}</p>;
  return (
    <div className="list">
      {rows.map((u) => (
        <button key={u.id} type="button" className="list-row req-item person-row" onClick={() => onOpen(u)}>
          <Initials name={u.name} />
          <div className="list-main">
            <p className="list-title">{u.name}</p>
            <p className="list-sub">
              {u.email} · {u.school_name ?? whereabouts(u)}{' '}
              · {Number(u.lessons)} lesson{Number(u.lessons) === 1 ? '' : 's'}
              {u.last_active_at ? `, active ${relativeDate(u.last_active_at).toLowerCase()}` : `, joined ${relativeDate(u.created_at).toLowerCase()}`}
            </p>
          </div>
          <span className="chip">{u.role === 'system_admin' ? 'Admin' : u.role === 'teacher' ? 'Teacher' : Number(u.is_owner) ? 'Owner' : 'Leader'}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Set up an organisation: its name and seats, and who owns it. The owner gets
 * an email to create their account and finish setup on the web.
 */
function NewOrganisationDialog({
  open,
  initialType,
  onClose,
  onCreated,
}: {
  open: boolean;
  initialType: 'organisation' | 'personal';
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [type, setType] = useState<'organisation' | 'personal'>(initialType);
  const [name, setName] = useState('');
  const [seats, setSeats] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setType(initialType);
      setName('');
      setSeats('');
      setOwnerName('');
      setOwnerEmail('');
      setError(null);
    }
  }, [open, initialType]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ id: string }>('/admin/schools', {
        method: 'POST',
        body: JSON.stringify({
          name,
          type,
          seatLimit: type === 'personal' ? 1 : seats.trim() === '' ? null : Number(seats),
          ownerName,
          ownerEmail,
        }),
      });
      onCreated(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const ready = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim());
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} title={type === 'personal' ? 'New personal account' : 'New organisation'}>
      <div className="segmented new-org-type" role="group" aria-label="Type of account">
        <button type="button" aria-pressed={type === 'organisation'} onClick={() => setType('organisation')}>
          Organisation
        </button>
        <button type="button" aria-pressed={type === 'personal'} onClick={() => setType('personal')}>
          Personal
        </button>
      </div>
      {type === 'personal' ? (
        <>
          <Field id="org-new-name" label="Account name" placeholder="e.g. Sam Foster" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Field id="org-new-owner-name" label="Their name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          <Field id="org-new-owner-email" label="Their email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
          <p className="muted">
            One person using EducAitor for themselves, with one seat. They&rsquo;ll get an email to create their account, then a short setup on the web: their
            role, framework and the terms, with no organisation or team questions.
          </p>
        </>
      ) : (
        <>
          <Field id="org-new-name" label="Organisation name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Field
            id="org-new-seats"
            label="Seats"
            inputMode="numeric"
            placeholder="Leave empty for no limit"
            value={seats}
            onChange={(e) => setSeats(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <Field id="org-new-owner-name" label="Owner’s name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          <Field id="org-new-owner-email" label="Owner’s email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
          <p className="muted">
            They&rsquo;ll get an email to create their account with that address and finish setting up the organisation (its details, framework and team) on the
            web. You can change the seats later.
          </p>
        </>
      )}
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div className="dialog-actions">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={create} loading={busy} disabled={!ready}>
          {type === 'personal' ? 'Create and invite them' : 'Create and invite the owner'}
        </Button>
      </div>
    </Dialog>
  );
}

/** Done, or what's holding setup up: no owner yet, answers missing, or the owner hasn't finished. */
function SetupChip({ school: s }: { school: SchoolRow }) {
  const state = s.setup ?? (s.onboarded_at ? 'done' : 'in_progress');
  if (state === 'done') return <span className="chip chip-accent">Done</span>;
  if (state === 'no_owner')
    return (
      <span className="chip" title="Nobody at the organisation can finish setup yet">
        No owner yet
      </span>
    );
  const missing = s.setup_missing ?? [];
  return (
    <span className="setup-cell">
      <span className="chip">Not yet</span>
      {missing.length ? <span className="setup-missing">Needs {missing.join(', ')}</span> : <span className="setup-missing">Owner to finish</span>}
    </span>
  );
}
